import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useState } from "react";

import { useChatChannel } from "../api/useChatChannel";
import { useChatMessages } from "../api/useChatMessages";
import { useEnsureChatChannel } from "../api/useEnsureChatChannel";
import { useSendFileMessage } from "../api/useSendFileMessage";
import { useSendTextMessage } from "../api/useSendTextMessage";
import { type Exclusion, useWorkerBlockedStatus } from "../api/useWorkerBlockedStatus";
import { useWorkerHasActiveChartingNoncompliance } from "../api/useWorkerHasActiveChartingNoncompliance";
import { createChannelUrl } from "../constants";
import { type ReplyContext } from "../types";
import { logChatMessageSent } from "../utils/logChatEvents";
import { useMarkAsUnreadHandlers } from "./useMarkAsUnreadHandlers";
import { useReplyState } from "./useReplyState";

interface UseChatPanelStateParams {
  workerId: string;
  workplaceId?: string;
  placementId?: string;
  /**
   * Pre-fetched GroupChannel from the caller (e.g. channel-list selection).
   * When provided, skips the internal `useChatChannel` fetch and the
   * `useEnsureChatChannel` mutation. When omitted, the hook fetches and
   * ensures the channel itself (panel + deep-link paths).
   */
  channel?: GroupChannel;
  /**
   * Optional modal state. When provided, `handleClose` closes the modal and
   * the panel unmounts on mark-unread (matching the existing `ChatPanel`
   * behavior). When omitted (page surface), `handleClose` only invokes
   * `onClose` and the internal latch suppresses auto-mark-read until the
   * conversation key changes.
   */
  modalState?: UseModalState;
  onClose?: () => void;
}

interface UseChatPanelStateResult {
  workplaceId: string;
  isAdmin: boolean;
  isWorkerSession: boolean;
  isLoading: boolean;
  isError: boolean;
  isBlocked: boolean;
  isSendingDisabled: boolean;
  messages: ReturnType<typeof useChatMessages>["messages"];
  hasMore: boolean;
  isLoadingMore: boolean;
  exclusion: Exclusion | undefined;
  replyTo: ReplyContext | null;
  handleClose: () => void;
  handleSend: (message: string) => void;
  handleFileSelect: (file: File) => void;
  handleRetry: () => void;
  handleMarkMessageAsUnread: (messageId: number) => void;
  handleMarkConversationAsUnread: () => void;
  canMarkConversationAsUnread: boolean;
  loadMore: () => void;
  setReplyTo: (context: ReplyContext) => void;
  clearReply: () => void;
}

export function useChatPanelState(params: UseChatPanelStateParams): UseChatPanelStateResult {
  const {
    workerId,
    workplaceId: workplaceIdFromCaller,
    placementId,
    channel: channelProperty,
    modalState,
    onClose,
  } = params;
  const session = useSession();
  const workplaceId = workplaceIdFromCaller ?? session.profile.userId;
  const isAdmin = Boolean(session.admin);
  const isWorkerSession = session.type === "AGENT";

  const { replyTo, setReplyTo, clearReply } = useReplyState();

  const {
    data: blockedStatus,
    isLoading: isLoadingBlockedStatus,
    isError: isErrorBlockedStatus,
    refetch: refetchBlockedStatus,
  } = useWorkerBlockedStatus({ workplaceId, workerId });

  const { hasActiveChartingNoncompliance, isLoading: isLoadingChartingNoncompliance } =
    useWorkerHasActiveChartingNoncompliance({ workplaceId, workerId });

  const { mutate: ensureChannel } = useEnsureChatChannel();

  const {
    data: fetchedChannel,
    isLoading: isLoadingChannel,
    isError: isErrorChannel,
    refetch: refetchChannel,
  } = useChatChannel({ workplaceId, workerId, placementId, enabled: !channelProperty });

  const channel = channelProperty ?? fetchedChannel ?? null;

  const baseUrl = createChannelUrl(workplaceId, workerId);
  const channelUrl = placementId ? `${baseUrl}_${placementId}` : baseUrl;

  // Once the user marks the conversation unread, suppress the auto-mark-read
  // effect so an incoming message doesn't silently re-mark it read while they
  // are still viewing it. Reset when the conversation key changes. Inert in
  // the panel path because the modal unmounts immediately on mark-unread.
  const [hasUserMarkedUnread, setHasUserMarkedUnread] = useState(false);
  useEffect(() => {
    setHasUserMarkedUnread(false);
    clearReply();
  }, [channelUrl, clearReply]);

  const {
    messages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
    hasMore,
    loadMore,
    isLoadingMore,
    refetch: refetchMessages,
  } = useChatMessages({ channel, autoMarkAsReadEnabled: !hasUserMarkedUnread });

  const { mutate: sendMessage, isLoading: isSending } = useSendTextMessage();
  const { mutate: sendFile, isLoading: isSendingFile } = useSendFileMessage();

  // Ensure the base channel exists when no pre-fetched channel was provided.
  // Channel-list selections already carry a channel; placement channels are
  // created server-side, so we only ensure the base workplaceId_workerId pair.
  useEffect(() => {
    if (!channelProperty && workplaceId && workerId) {
      ensureChannel({ workplaceId, workerId });
    }
  }, [channelProperty, ensureChannel, workplaceId, workerId]);

  const handleClose = useCallback(() => {
    modalState?.closeModal();
    onClose?.();
  }, [modalState, onClose]);

  const handleSend = useCallback(
    (message: string) => {
      if (!channel) {
        return;
      }
      sendMessage(
        { channel, message, parentMessageId: replyTo?.messageId },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "text",
              isReply: Boolean(replyTo),
            });
            clearReply();
          },
        }
      );
    },
    [channel, sendMessage, replyTo, clearReply, workerId, workplaceId]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!channel) {
        return;
      }
      sendFile(
        { channel, file },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "file",
              isReply: false,
            });
          },
        }
      );
    },
    [channel, sendFile, workerId, workplaceId]
  );

  const isLoading =
    (!channelProperty && isLoadingChannel) ||
    isLoadingMessages ||
    isLoadingBlockedStatus ||
    isLoadingChartingNoncompliance;
  // Charting-noncompliance errors graceful-degrade to `false`, which keeps the
  // block check strict — so a charting error doesn't surface as an `isError`
  // on the panel.
  const isError = (!channelProperty && isErrorChannel) || isErrorMessages || isErrorBlockedStatus;
  const isBlocked = (blockedStatus?.isBlocked ?? false) && !hasActiveChartingNoncompliance;

  const handleRetry = useCallback(() => {
    if (!channelProperty) {
      void refetchChannel();
    }
    void refetchBlockedStatus();
    void refetchMessages();
  }, [channelProperty, refetchChannel, refetchBlockedStatus, refetchMessages]);

  const afterMarkUnread = useCallback(() => {
    setHasUserMarkedUnread(true);
    modalState?.closeModal();
    onClose?.();
  }, [modalState, onClose]);

  const { handleMarkMessageAsUnread, handleMarkConversationAsUnread, canMarkConversationAsUnread } =
    useMarkAsUnreadHandlers({
      channel,
      workplaceId,
      workerId,
      messages,
      onAfterMark: afterMarkUnread,
    });

  return {
    workplaceId,
    isAdmin,
    isWorkerSession,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled: isSending || isSendingFile || !channel,
    messages,
    hasMore,
    isLoadingMore,
    exclusion: blockedStatus?.exclusion,
    replyTo,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    loadMore,
    setReplyTo,
    clearReply,
  };
}
